import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms'
import { Router } from '@angular/router'

import { DatePickerModule } from 'primeng/datepicker'

import {
  FLX_MODAL_DATA,
  FlxButtonModule,
  FlxFileUploadComponent,
  FlxFileUploadEvent,
  FlxFormControlModule,
  FlxIconComponent,
  FlxModalData,
  FlxModalModule,
  FlxModalService,
} from '@flowx/angular-ui-toolkit'
import { DEFAULT_LANGUAGE, StartMode } from '@flowx/core-sdk'
import {
  PDObject,
  ProcessDesignerAction,
} from '@flowx/designer/modules/analytics/analytics-process-designer.entity'
import { AnalyticsService } from '@flowx/designer/modules/analytics/analytics.service'
import { ApiTheme } from '@flowx/designer/modules/cms/pages/themes/theme.dictionary'
import { ProcessDefinition } from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { CodeEditorComponent } from '@flowx/designer/modules/script-builder/components/code-editor/code-editor.component'
import { DocumentPluginApi } from '@flowx/designer/shared/api/document-plugin.api'
import {
  ApplicationResourceType,
  Build,
  BuildManifestResourceSummary,
  StartDataEntryPoint,
  matchesEntryPoint,
} from '@flowx/designer/shared/entities/application.entity'
import { ProcessDefinitionService } from '@flowx/designer/shared/services/process-definition.service'
import { UrlParametersStoreService } from '@flowx/designer/shared/services/url-parameters-store.service'

enum StartType {
  START_FROM_EMAIL = 'startFromEmail',
  START_FROM_EVENT = 'startFromEvent',
}

export type ResourceKind = 'process' | 'ui-flow'
export type LaunchMode = 'select' | 'entrypoint'

export type StartDataLaunchOptions = { resourceKind: ResourceKind } & (
  { launchMode: 'select' } | { launchMode: 'entrypoint'; entryPoint: StartDataEntryPoint }
)

export type StartDataFromBuild = {
  build: Build
  applicationResources: BuildManifestResourceSummary[]
  themes: { list: ApiTheme[]; selected?: ApiTheme }
} & StartDataLaunchOptions

/**
 * Run on Config: a project version's entry point process, started without a build. The dialog reads
 * as the usual Start Process.
 */
export type StartDataFromVersion = {
  appVersionId: string
  process: Pick<ProcessDefinition, 'resourceId' | 'resourceName'>
  themes: { list: ApiTheme[]; selected?: ApiTheme }
}

@Component({
  selector: 'app-start-data-dialog',
  imports: [
    FlxModalModule,
    FlxFormControlModule,
    ReactiveFormsModule,
    FlxButtonModule,
    CodeEditorComponent,
    FlxIconComponent,
    FlxFileUploadComponent,
    DatePickerModule,
  ],
  templateUrl: './start-data-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./start-data-dialog.component.scss'],
})
export class StartDataDialogComponent implements OnInit {
  #urlParamsStore = inject(UrlParametersStoreService)
  #analyticsService = inject(AnalyticsService)
  #processDefinitionService = inject(ProcessDefinitionService)
  #documentPluginApi = inject(DocumentPluginApi)
  #destroyRef = inject(DestroyRef)
  #data: FlxModalData = inject(FLX_MODAL_DATA)
  #router = inject(Router)
  #flxModalService = inject(FlxModalService)
  #fb = inject(FormBuilder)

  #applicationResources = signal<BuildManifestResourceSummary[]>([])
  #build?: Build = null
  #entryPoint: StartDataEntryPoint = null
  #appVersionId: string = null

  resourceKind = signal<ResourceKind>('process')
  launchMode = signal<LaunchMode>('select')

  isUiFlow = computed(() => this.resourceKind() === 'ui-flow')

  resourceList = computed(() => {
    const applicationResources = this.#applicationResources()
    const type = this.isUiFlow() ? ApplicationResourceType.UiFlow : ApplicationResourceType.Process
    return applicationResources.filter((resource) => resource.resourceType === type)
  })

  title = computed(() => {
    if (!this.runFromBuild) {
      return 'Start Process'
    }
    if (this.launchMode() === 'entrypoint') {
      return 'Test Build'
    }
    return this.isUiFlow() ? 'Test UI Flow' : 'Test Process'
  })
  subtitle = computed(() => {
    if (!this.runFromBuild) {
      return this.process?.resourceName ?? ''
    }
    if (this.launchMode() === 'entrypoint') {
      return 'Launch the solution using the configured entry point'
    }
    return this.isUiFlow() ? 'Select a UI Flow to test' : 'Select a Process to test'
  })
  ctaLabel = computed(() => {
    if (this.runFromBuild && this.launchMode() === 'entrypoint') {
      return 'Launch Solution'
    }
    return this.isUiFlow() ? 'Start UI Flow' : 'Start Process'
  })
  selectorLabel = computed(() => (this.isUiFlow() ? 'UI Flow' : 'Process'))
  showResourceSelector = computed(() => this.runFromBuild)

  themes: ApiTheme[] = []
  language: string
  uploadedFiles: { filename: string; id: string }[] = []

  startDataForm: UntypedFormGroup = new UntypedFormGroup({
    startData: new UntypedFormControl('{}', [Validators.required]),
  })

  emailFormGroup = this.#fb.group({
    subject: [''],
    sender: [''],
    dateTime: [new Date()],
    body: [''],
  })

  resourceFilter = signal<string>('')
  filteredResources = computed(() => {
    const resourceList = this.resourceList()
    const query = this.resourceFilter() || ''
    return query?.length
      ? resourceList.filter((item) =>
          item.resourceName.toLowerCase().includes(query?.toLowerCase())
        )
      : resourceList
  })

  process: Partial<ProcessDefinition> = null

  editorOptions = {
    language: 'json',
    autoIndent: 'full',
    formatOnPaste: true,
    formatOnType: true,
    automaticLayout: true,
    glyphMargin: false,
    lineNumbersMinChars: 0,
    quickSuggestions: false,
    overviewRulerLanes: 0,
    hideCursorInOverviewRuler: true,
    overviewRulerBorder: false,
  }

  startType = StartType
  hasEmailTriggerNode = false
  hasWebhookTriggerNode = false
  hasStartNode = false

  themeFC = new FormControl<string>(null)
  resourceFC = new FormControl<string>(null)
  startNodeFC = new FormControl<StartType>(StartType.START_FROM_EVENT)
  runFromBuild = false
  runFromVersion = false

  ngOnInit(): void {
    const fromBuild = this.#data.fromBuild as StartDataFromBuild | undefined
    const fromVersion = this.#data.fromVersion as StartDataFromVersion | undefined
    if (fromBuild) {
      this.#initFromBuild(fromBuild)
    } else if (fromVersion) {
      this.#initFromVersion(fromVersion)
    } else {
      this.#initFromProcess(this.#data as any)
    }
  }

  onFileUpload({ file }: FlxFileUploadEvent): void {
    this.#documentPluginApi
      .uploadTempFile(file)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((response) => {
        this.uploadedFiles = [...this.uploadedFiles, { filename: file.name, id: response.id }]
      })
  }

  removeFile(index: number): void {
    this.uploadedFiles = this.uploadedFiles.filter((_, i) => i !== index)
  }

  start(): void {
    if (this.isUiFlow()) {
      this.#startUiFlow()
      return
    }
    this.#startProcess()
  }

  closeModal(): void {
    this.#flxModalService.hide()
  }

  #startProcess(): void {
    this.#analyticsService.trackProcessDesigner({
      event: { object: PDObject.PROCESS_DESIGNER, action: ProcessDesignerAction.PROCESS_START },
    })
    const urlParams = this.#urlParamsStore.urlParameters

    let processResourceId: string
    let appVersionId: string
    let processName: string
    let extraState: Record<string, any> = {}

    if (this.runFromBuild) {
      processResourceId = this.resourceFC.value
      processName = this.resourceList().find(
        (p) => p.resourceId === processResourceId
      )?.resourceName
      if (!processName) {
        console.error('Start process failed, process not found')
        return
      }
      appVersionId = this.#build.applicationVersionId
      extraState = {
        buildId: this.#build.applicationVersionId,
        fromBuild: this.runFromBuild,
      }
    } else if (this.runFromVersion) {
      // The entry point, not the process open in the editor, if any.
      processResourceId = this.process.resourceId
      processName = this.process.resourceName
      appVersionId = this.#appVersionId
    } else {
      processResourceId = urlParams.selectedProcessResourceId || this.process.resourceId
      appVersionId = urlParams.appVersionId
      processName = this.process.resourceName
    }

    const processStartData =
      this.startNodeFC.value === this.startType.START_FROM_EMAIL
        ? this.#getEmailTriggerData()
        : this.startDataForm.get('startData').value

    this.#router.navigate(
      [
        'guard',
        'workspace',
        urlParams.workspaceId,
        'projects',
        urlParams.appId,
        'runtime',
        appVersionId,
        'processes',
        processResourceId,
        'instance',
      ],
      {
        queryParams: {
          themeId: this.themeFC.value,
          locale: this.#data.locale,
          language: this.language,
          runFromBuild: this.runFromBuild,
        },
        state: {
          processName,
          processStartData,
          debugCustomComponents: true,
          startMode:
            this.startNodeFC.value === this.startType.START_FROM_EMAIL
              ? StartMode.EMAIL_TRIGGER
              : StartMode.MANUAL,
          ...extraState,
        },
      }
    )
    this.closeModal()
  }

  #startUiFlow(): void {
    const urlParams = this.#urlParamsStore.urlParameters
    const uiFlowResourceId = this.resourceFC.value
    if (!uiFlowResourceId) {
      console.error('Start UI Flow failed, UI Flow not found')
      return
    }
    const resourceDefinitionId = this.resourceList().find(
      (resource) => resource.resourceId === uiFlowResourceId
    )?.resourceDefinitionId

    this.#router.navigate(
      [
        'guard',
        'workspace',
        urlParams.workspaceId,
        'projects',
        urlParams.appId,
        'runtime',
        this.#build.applicationVersionId,
        'ui-flow',
        uiFlowResourceId,
        'instance',
      ],
      {
        queryParams: {
          themeId: this.themeFC.value,
          locale: this.#data.locale,
          language: this.language,
          runFromBuild: true,
          resourceDefinitionId,
        },
        state: {
          startParams: this.#parseStartParams(),
        },
      }
    )
    this.closeModal()
  }

  #parseStartParams(): Record<string, unknown> {
    try {
      const parsed = JSON.parse(this.startDataForm.get('startData').value?.trim() || '{}')
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
      return {}
    }
  }

  #fetchNodeParameters(processResourceId: string, applicationVersionId?: string): void {
    this.#processDefinitionService
      .getNodeParameters(processResourceId, applicationVersionId)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((response) => {
        this.hasEmailTriggerNode = response.hasEmailTriggerStart
        this.hasWebhookTriggerNode = response.hasWebhookTriggerStart
        this.hasStartNode = response.hasManualStart

        const startFromEmail =
          this.hasEmailTriggerNode && !this.hasStartNode && !this.hasWebhookTriggerNode
        this.startNodeFC.setValue(
          startFromEmail ? this.startType.START_FROM_EMAIL : this.startType.START_FROM_EVENT
        )

        this.startDataForm
          .get('startData')
          .setValue(JSON.stringify(response.jsonStartExample || {}, undefined, 2))
      })
  }

  #getEmailTriggerData(): Record<string, any> {
    const formValue = this.emailFormGroup.value
    return {
      subject: formValue.subject,
      sender: formValue.sender,
      dateTime: formValue.dateTime?.toISOString(),
      body: formValue.body,
      attachments: this.uploadedFiles.map((file) => file.id),
    }
  }

  #initFromProcess(data: {
    fromProcess: ProcessDefinition & { theme: { list: ApiTheme[]; selected: ApiTheme } }
    hasEmailTriggerNode: boolean
    hasWebhookTriggerNode: boolean
    hasStartNode: boolean
  }): void {
    const { resourceId, resourceName, state, theme } = data.fromProcess
    this.process = {
      resourceId,
      resourceName,
      state,
    }

    this.hasEmailTriggerNode = data.hasEmailTriggerNode
    this.hasWebhookTriggerNode = data.hasWebhookTriggerNode
    this.hasStartNode = data.hasStartNode

    if (this.hasEmailTriggerNode && !this.hasStartNode && !this.hasWebhookTriggerNode) {
      this.startNodeFC.setValue(this.startType.START_FROM_EMAIL)
    }

    if (this.hasStartNode || this.hasWebhookTriggerNode) {
      this.#fetchNodeParameters(this.process.resourceId)
    }

    this.themes = theme.list
    this.themeFC.setValue(theme.selected.flowxUuid)
    this.language = this.#data.language || DEFAULT_LANGUAGE
  }

  #initFromBuild(data: StartDataFromBuild): void {
    this.runFromBuild = true
    const { build, applicationResources, themes, resourceKind, launchMode } = data
    this.themes = themes.list
    this.#applicationResources.set(applicationResources)
    this.#build = build
    this.resourceKind.set(resourceKind)
    this.launchMode.set(launchMode)
    this.themeFC.setValue(build.applicationSettings.themeId)

    this.language =
      build.applicationSettings.languages?.find((language) => language.isDefault)?.code ||
      DEFAULT_LANGUAGE

    if (data.launchMode === 'entrypoint') {
      this.#entryPoint = data.entryPoint
      this.#initEntryPoint()
    } else if (this.isUiFlow()) {
      this.#initResourceSelect()
    } else {
      this.#initProcessSelect()
    }
  }

  #initFromVersion(data: StartDataFromVersion): void {
    this.runFromVersion = true
    this.#appVersionId = data.appVersionId
    this.process = data.process
    this.themes = data.themes.list
    this.themeFC.setValue(data.themes.selected?.flowxUuid ?? null)
    this.language = this.#data.language || DEFAULT_LANGUAGE
    this.#fetchNodeParameters(data.process.resourceId, data.appVersionId)
  }

  #initProcessSelect(): void {
    const initialProcess = this.resourceList()[0]
    if (initialProcess) {
      this.resourceFC.setValue(initialProcess.resourceId, { emitEvent: false })
      this.#fetchNodeParameters(initialProcess.resourceId, initialProcess.applicationVersionId)
    }

    this.resourceFC.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((resourceId) => {
        if (resourceId) {
          const appVersionId = this.resourceList().find(
            (p) => p.resourceId === resourceId
          )?.applicationVersionId
          this.#fetchNodeParameters(resourceId, appVersionId)
        }
      })
  }

  #initResourceSelect(): void {
    const initialResource = this.resourceList()[0]
    if (initialResource) {
      this.resourceFC.setValue(initialResource.resourceId, { emitEvent: false })
    }
    this.startDataForm.get('startData').setValue('{}')
  }

  #initEntryPoint(): void {
    const resource = this.resourceList().find(matchesEntryPoint(this.#entryPoint))
    const resourceId = resource?.resourceId ?? this.#entryPoint.resId
    this.resourceFC.setValue(resourceId, { emitEvent: false })
    this.resourceFC.disable({ emitEvent: false })
    if (this.isUiFlow()) {
      this.startDataForm.get('startData').setValue('{}')
    } else {
      this.#fetchNodeParameters(resourceId, this.#build.applicationVersionId)
    }
  }
}
